import { SFX, type SfxName } from './sfx.js';

/**
 * Gestion du son.
 *
 * Politique d'autoplay : l'AudioContext n'est JAMAIS cree au chargement. Il naît
 * au premier geste utilisateur reel (pointerdown / keydown / touchend), sinon
 * les navigateurs le creent suspendu et tous les sons suivants sont muets sans
 * le moindre message d'erreur.
 *
 * Graphe :
 *   sfx  --> sfxGain  --\
 *                        >-- masterGain --> destination
 *   music --> musicGain -/
 */

export type AudioChannel = 'music' | 'sfx';

export interface AudioSettingsSnapshot {
  readonly musicVolume: number;
  readonly sfxVolume: number;
  readonly muted: boolean;
}

interface Nodes {
  readonly ctx: AudioContext;
  readonly master: GainNode;
  readonly music: GainNode;
  readonly sfx: GainNode;
}

type AudioContextConstructor = new (options?: AudioContextOptions) => AudioContext;

function audioContextCtor(): AudioContextConstructor | null {
  const scope = window as unknown as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/** Rampe courte : evite les clics de commutation de gain. */
const RAMP = 0.04;

export class AudioManager {
  #nodes: Nodes | null = null;
  #musicSource: AudioBufferSourceNode | null = null;
  #musicBuffer: AudioBuffer | null = null;
  #settings: AudioSettingsSnapshot = { musicVolume: 0.4, sfxVolume: 0.7, muted: false };
  #unlockBound = false;
  /** Anti-spam : un meme effet ne se declenche pas deux fois en 30 ms. */
  readonly #lastPlayed = new Map<SfxName, number>();

  /**
   * Branche les ecouteurs qui creeront l'AudioContext au premier geste.
   * A appeler une fois au demarrage : ne cree rien par lui-meme.
   */
  installUnlockListeners(target: EventTarget = window): void {
    if (this.#unlockBound) return;
    this.#unlockBound = true;

    const unlock = (): void => {
      void this.resume();
    };
    const options: AddEventListenerOptions = { passive: true };
    target.addEventListener('pointerdown', unlock, options);
    target.addEventListener('touchend', unlock, options);
    target.addEventListener('keydown', unlock, options);
  }

  get unlocked(): boolean {
    return this.#nodes !== null && this.#nodes.ctx.state === 'running';
  }

  /** Cree (ou reprend) l'AudioContext. Sans effet si l'API n'existe pas. */
  async resume(): Promise<void> {
    const nodes = this.#ensureNodes();
    if (!nodes) return;
    if (nodes.ctx.state === 'suspended') {
      try {
        await nodes.ctx.resume();
      } catch {
        // Geste juge insuffisant par le navigateur : on reessaiera au suivant.
        return;
      }
    }
    this.#applyGains();
    if (this.#musicBuffer && !this.#musicSource) this.#startMusic();
  }

  #ensureNodes(): Nodes | null {
    if (this.#nodes) return this.#nodes;

    const Ctor = audioContextCtor();
    if (!Ctor) return null;

    const ctx = new Ctor({ latencyHint: 'interactive' });
    const master = ctx.createGain();
    const music = ctx.createGain();
    const sfx = ctx.createGain();

    master.gain.value = 0;
    music.gain.value = 0;
    sfx.gain.value = 0;
    music.connect(master);
    sfx.connect(master);
    master.connect(ctx.destination);

    this.#nodes = { ctx, master, music, sfx };
    this.#applyGains();
    return this.#nodes;
  }

  applySettings(settings: AudioSettingsSnapshot): void {
    this.#settings = {
      musicVolume: clamp01(settings.musicVolume),
      sfxVolume: clamp01(settings.sfxVolume),
      muted: settings.muted,
    };
    this.#applyGains();
  }

  #applyGains(): void {
    const nodes = this.#nodes;
    if (!nodes) return;
    const now = nodes.ctx.currentTime;
    const master = this.#settings.muted ? 0 : 1;
    ramp(nodes.master.gain, master, now);
    ramp(nodes.music.gain, this.#settings.musicVolume, now);
    // Les effets sont mixes plus bas que la musique : ils ponctuent, ils ne
    // portent pas l'ambiance.
    ramp(nodes.sfx.gain, this.#settings.sfxVolume * 0.6, now);
  }

  /** Joue un effet. Silencieux tant que le contexte n'est pas debloque. */
  play(name: SfxName): void {
    if (this.#settings.muted) return;
    const nodes = this.#nodes;
    if (!nodes || nodes.ctx.state !== 'running') return;

    const now = performance.now();
    const last = this.#lastPlayed.get(name);
    if (last !== undefined && now - last < 30) return;
    this.#lastPlayed.set(name, now);

    SFX[name]({ ctx: nodes.ctx, out: nodes.sfx, at: nodes.ctx.currentTime });
  }

  // ------------------------------------------------------------------------
  // Musique : emplacement pret, inactif tant qu'aucune URL n'est fournie.
  // ------------------------------------------------------------------------

  /**
   * Charge une piste et la joue en boucle.
   *
   * Aucune piste n'est livree pour l'instant : cette methode existe pour que
   * brancher une boucle plus tard soit un one-liner. Rappel Discord : une URL
   * externe serait coupee par la CSP, la piste devra etre servie same-origin
   * (ex. `/audio/salon.mp3`) ou via `/.proxy/<prefix>`.
   */
  async loadMusic(url: string | null): Promise<boolean> {
    if (!url) return false;
    const nodes = this.#ensureNodes();
    if (!nodes) return false;

    try {
      const response = await fetch(url);
      if (!response.ok) return false;
      const bytes = await response.arrayBuffer();
      this.#musicBuffer = await nodes.ctx.decodeAudioData(bytes);
    } catch {
      return false;
    }

    if (nodes.ctx.state === 'running') this.#startMusic();
    return true;
  }

  #startMusic(): void {
    const nodes = this.#nodes;
    const buffer = this.#musicBuffer;
    if (!nodes || !buffer) return;
    this.stopMusic();

    const source = nodes.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(nodes.music);
    source.start();
    this.#musicSource = source;
  }

  stopMusic(): void {
    if (!this.#musicSource) return;
    try {
      this.#musicSource.stop();
    } catch {
      // Deja arretee.
    }
    this.#musicSource.disconnect();
    this.#musicSource = null;
  }
}

function ramp(param: AudioParam, value: number, now: number): void {
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(value, now + RAMP);
}

export const audio = new AudioManager();
